import React, { useState, useEffect } from 'react';

interface DashboardStats {
  totalProducts: number;
  totalBatches: number;
  thisMonthProducts: number;
  thisMonthScans: number;
  currentBill: number;
  recentActivity: Array<{
    id: string;
    type: 'scan' | 'batch_upload' | 'product_added' | 'counterfeit_detected';
    description: string;
    timestamp: string;
    location?: string;
    severity?: 'low' | 'medium' | 'high';
  }>;
  monthlyTrends: {
    scansGrowth: number;
    productsGrowth: number;
    revenueProtected: number;
    counterfeitsPrevented: number;
  };
  topProducts: Array<{
    name: string;
    category: string;
    scans: number;
    trend: 'up' | 'down' | 'stable';
  }>;
}

const ManufacturerDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    totalBatches: 0,
    thisMonthProducts: 0,
    thisMonthScans: 0,
    currentBill: 0,
    recentActivity: [],
    monthlyTrends: {
      scansGrowth: 0,
      productsGrowth: 0,
      revenueProtected: 0,
      counterfeitsPrevented: 0
    },
    topProducts: []
  });
  const [loading, setLoading] = useState(true);

  // Animation styles
  const animationStyles = `
    .fade-in-up {
      animation: fadeInUp 0.8s ease-out forwards;
    }
    
    .slide-in-left {
      animation: slideInLeft 0.8s ease-out forwards;
    }
    
    .scale-in {
      animation: scaleIn 0.6s ease-out forwards;
    }
    
    .pulse {
      animation: pulse 2s ease-in-out infinite;
    }
    
    .hover-lift {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .hover-lift:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-50px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.9);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Enhanced simulated data for universal platform
      setTimeout(() => {
        setStats({
          totalProducts: 24,
          totalBatches: 89,
          thisMonthProducts: 4850,
          thisMonthScans: 2847,
          currentBill: 4850,
          recentActivity: [
            {
              id: '1',
              type: 'scan',
              description: 'Premium Vodka 750ml verified successfully',
              timestamp: '12 minutes ago',
              location: 'Nairobi, Kenya',
              severity: 'low'
            },
            {
              id: '2',
              type: 'counterfeit_detected',
              description: 'Counterfeit attempt blocked: Organic Face Cream',
              timestamp: '1 hour ago',
              location: 'Mombasa, Kenya',
              severity: 'high'
            },
            {
              id: '3',
              type: 'batch_upload',
              description: 'New batch uploaded: BATCH-NOV-2024-001',
              timestamp: '3 hours ago',
              severity: 'low'
            },
            {
              id: '4',
              type: 'scan',
              description: 'Smartphone XR Pro authentication successful',
              timestamp: '5 hours ago',
              location: 'Kisumu, Kenya',
              severity: 'low'
            },
            {
              id: '5',
              type: 'product_added',
              description: 'New product registered: Premium Coffee Beans 500g',
              timestamp: '1 day ago',
              severity: 'medium'
            }
          ],
          monthlyTrends: {
            scansGrowth: 23.5,
            productsGrowth: 18.2,
            revenueProtected: 125000,
            counterfeitsPrevented: 47
          },
          topProducts: [
            { name: 'Premium Vodka 750ml', category: 'Alcohol & Beverages', scans: 1247, trend: 'up' },
            { name: 'Organic Face Cream 50ml', category: 'Cosmetics', scans: 892, trend: 'up' },
            { name: 'Smartphone XR Pro', category: 'Electronics', scans: 634, trend: 'stable' },
            { name: 'Premium Coffee Beans', category: 'Food & Snacks', scans: 423, trend: 'up' }
          ]
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '24px',
          padding: '60px',
          textAlign: 'center',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <h3 style={{ color: '#1e293b', margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
            Loading Dashboard
          </h3>
          <p style={{ color: '#64748b', margin: 0, fontSize: '16px' }}>
            Fetching your latest analytics and insights
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>{animationStyles}</style>
      
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)`,
      }}></div>

      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '40px 20px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Header */}
          <div className="fade-in-up" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '40px 48px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '24px'
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                    </svg>
                  </div>
                  <div>
                    <h1 style={{
                      fontSize: '2.5rem',
                      fontWeight: '800',
                      color: '#1e293b',
                      margin: 0,
                      lineHeight: '1'
                    }}>
                      Manufacturer Dashboard
                    </h1>
                    <p style={{
                      color: '#64748b',
                      fontSize: '18px',
                      margin: 0
                    }}>
                      Welcome back! Here's your ScanOnce 2.0 overview.
                    </p>
                  </div>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <a
                  href="/manufacturer/batches"
                  className="hover-lift"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: 'white',
                    padding: '16px 24px',
                    textDecoration: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7,10 12,15 17,10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Upload Batch
                </a>
                <a
                  href="/manufacturer/products"
                  className="hover-lift"
                  style={{
                    backgroundColor: 'white',
                    color: '#475569',
                    padding: '16px 24px',
                    textDecoration: 'none',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                  Manage Products
                </a>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="slide-in-left" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '40px'
          }}>
            {[
              {
                title: 'Total Products',
                value: stats.totalProducts,
                subtitle: 'Active product lines',
                icon: '📦',
                color: '#3b82f6',
                bgColor: '#eff6ff'
              },
              {
                title: 'Total Batches',
                value: stats.totalBatches,
                subtitle: 'Production batches uploaded',
                icon: '🏭',
                color: '#22c55e',
                bgColor: '#f0fdf4'
              },
              {
                title: 'This Month',
                value: stats.thisMonthProducts.toLocaleString(),
                subtitle: 'Products protected',
                icon: '📈',
                color: '#f59e0b',
                bgColor: '#fef3c7',
                trend: `+${stats.monthlyTrends.productsGrowth}%`
              },
              {
                title: 'Monthly Scans',
                value: stats.thisMonthScans.toLocaleString(),
                subtitle: 'Verification requests',
                icon: '🔍',
                color: '#8b5cf6',
                bgColor: '#f3e8ff',
                trend: `+${stats.monthlyTrends.scansGrowth}%`
              },
              {
                title: 'Current Bill',
                value: `KSh ${stats.currentBill.toLocaleString()}`,
                subtitle: '@ KSh 1 per product',
                icon: '💰',
                color: '#ef4444',
                bgColor: '#fef2f2'
              }
            ].map((stat, index) => (
              <div key={index} className="hover-lift" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                padding: '32px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                animationDelay: `${index * 0.1}s`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}>
                  <h3 style={{
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    margin: 0
                  }}>
                    {stat.title}
                  </h3>
                  <div style={{
                    backgroundColor: stat.bgColor,
                    padding: '12px',
                    borderRadius: '12px',
                    fontSize: '20px'
                  }}>
                    {stat.icon}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <p style={{
                    fontSize: '2.5rem',
                    fontWeight: '800',
                    color: '#1e293b',
                    margin: 0,
                    lineHeight: '1'
                  }}>
                    {stat.value}
                  </p>
                  {stat.trend && (
                    <span style={{
                      color: '#22c55e',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: '#f0fdf4',
                      padding: '4px 8px',
                      borderRadius: '8px'
                    }}>
                      {stat.trend}
                    </span>
                  )}
                </div>
                <p style={{
                  color: '#64748b',
                  fontSize: '14px',
                  margin: 0
                }}>
                  {stat.subtitle}
                </p>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '40px',
            marginBottom: '40px'
          }}>
            {/* Recent Activity */}
            <div className="scale-in" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  color: '#1e293b',
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Recent Activity
                </h3>
                <a
                  href="/manufacturer/analytics"
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#1e40af'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
                >
                  View All
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {stats.recentActivity.map((activity, index) => (
                  <div
                    key={activity.id}
                    className="hover-lift"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      animationDelay: `${index * 0.1}s`
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      background: activity.type === 'scan' ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : 
                                 activity.type === 'batch_upload' ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : 
                                 activity.type === 'counterfeit_detected' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                                 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white'
                    }}>
                      {activity.type === 'scan' ? '🔍' : 
                       activity.type === 'batch_upload' ? '📦' : 
                       activity.type === 'counterfeit_detected' ? '🚨' : '➕'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        color: '#1e293b',
                        fontSize: '15px',
                        fontWeight: '600',
                        margin: '0 0 4px 0'
                      }}>
                        {activity.description}
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          color: '#64748b',
                          fontSize: '13px'
                        }}>
                          {activity.timestamp}
                        </span>
                        {activity.location && (
                          <>
                            <span style={{ color: '#cbd5e1' }}>•</span>
                            <span style={{
                              color: '#64748b',
                              fontSize: '13px'
                            }}>
                              {activity.location}
                            </span>
                          </>
                        )}
                        {activity.severity && activity.severity !== 'low' && (
                          <span style={{
                            background: activity.severity === 'high' ? '#ef4444' : '#f59e0b',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {activity.severity}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Products */}
            <div className="scale-in" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <h3 style={{
                color: '#1e293b',
                fontSize: '1.3rem',
                fontWeight: '600',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
                </svg>
                Top Performing Products
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {stats.topProducts.map((product, index) => (
                  <div
                    key={index}
                    className="hover-lift"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        color: '#1e293b',
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 4px 0'
                      }}>
                        {product.name}
                      </h4>
                      <p style={{
                        color: '#3b82f6',
                        fontSize: '12px',
                        fontWeight: '500',
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {product.category}
                      </p>
                    </div>
                    <div style={{
                      textAlign: 'right',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div>
                        <div style={{
                          color: '#1e293b',
                          fontSize: '18px',
                          fontWeight: '700'
                        }}>
                          {product.scans.toLocaleString()}
                        </div>
                        <div style={{
                          color: '#64748b',
                          fontSize: '12px'
                        }}>
                          scans
                        </div>
                      </div>
                      <div style={{
                        color: product.trend === 'up' ? '#22c55e' : 
                               product.trend === 'down' ? '#ef4444' : '#64748b',
                        fontSize: '16px'
                      }}>
                        {product.trend === 'up' ? '📈' : 
                         product.trend === 'down' ? '📉' : '➡️'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="fade-in-up" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            marginBottom: '40px'
          }}>
            <h3 style={{
              color: '#1e293b',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
              </svg>
              Quick Actions
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              {[
                {
                  href: '/manufacturer/products',
                  icon: '📦',
                  title: 'Add Product',
                  subtitle: 'Register new product line',
                  color: '#3b82f6'
                },
                {
                  href: '/manufacturer/batches',
                  icon: '🏭',
                  title: 'Upload Batch',
                  subtitle: 'Add production batch',
                  color: '#22c55e'
                },
                {
                  href: '/manufacturer/analytics',
                  icon: '📊',
                  title: 'View Analytics',
                  subtitle: 'Detailed scan insights',
                  color: '#8b5cf6'
                },
                {
                  href: '/manufacturer/billing',
                  icon: '💳',
                  title: 'Billing',
                  subtitle: 'View invoices & payments',
                  color: '#f59e0b'
                }
              ].map((action, index) => (
                <a
                  key={index}
                  href={action.href}
                  className="hover-lift"
                  style={{
                    display: 'block',
                    padding: '24px',
                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    animationDelay: `${index * 0.1}s`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = action.color;
                    e.currentTarget.style.background = `${action.color}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)';
                  }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: `linear-gradient(135deg, ${action.color} 0%, ${action.color}dd 100%)`,
                    borderRadius: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    fontSize: '24px'
                  }}>
                    {action.icon}
                  </div>
                  <h4 style={{
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: '0 0 8px 0'
                  }}>
                    {action.title}
                  </h4>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    {action.subtitle}
                  </p>
                </a>
              ))}
            </div>
          </div>

          {/* Monthly Insights */}
          <div className="slide-in-left" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            marginBottom: '40px'
          }}>
            <h3 style={{
              color: '#1e293b',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5c0-1.66-4-3-9-3S3 3.34 3 5z"/>
                <path d="M3 12v7c0 1.66 4 3 9 3s9-1.34 9-3v-7"/>
              </svg>
              Monthly Business Insights
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px'
            }}>
              <div style={{
                padding: '24px',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: '1px solid #bbf7d0',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  💰
                </div>
                <h4 style={{
                  color: '#166534',
                  fontSize: '2rem',
                  fontWeight: '700',
                  margin: '0 0 8px 0'
                }}>
                  KSh {stats.monthlyTrends.revenueProtected.toLocaleString()}
                </h4>
                <p style={{
                  color: '#166534',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0
                }}>
                  Revenue Protected from Counterfeits
                </p>
              </div>

              <div style={{
                padding: '24px',
                background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                border: '1px solid #fca5a5',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  🛡️
                </div>
                <h4 style={{
                  color: '#991b1b',
                  fontSize: '2rem',
                  fontWeight: '700',
                  margin: '0 0 8px 0'
                }}>
                  {stats.monthlyTrends.counterfeitsPrevented}
                </h4>
                <p style={{
                  color: '#991b1b',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0
                }}>
                  Counterfeit Attempts Blocked
                </p>
              </div>

              <div style={{
                padding: '24px',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: '1px solid #93c5fd',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  📈
                </div>
                <h4 style={{
                  color: '#1e40af',
                  fontSize: '2rem',
                  fontWeight: '700',
                  margin: '0 0 8px 0'
                }}>
                  +{stats.monthlyTrends.scansGrowth}%
                </h4>
                <p style={{
                  color: '#1e40af',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0
                }}>
                  Monthly Scan Growth
                </p>
              </div>

              <div style={{
                padding: '24px',
                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                border: '1px solid #c4b5fd',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  🌍
                </div>
                <h4 style={{
                  color: '#7c2d12',
                  fontSize: '2rem',
                  fontWeight: '700',
                  margin: '0 0 8px 0'
                }}>
                  12
                </h4>
                <p style={{
                  color: '#7c2d12',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0
                }}>
                  Countries with Verified Scans
                </p>
              </div>
            </div>
          </div>

          {/* Help Section - Enhanced */}
          <div className="scale-in" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '48px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '32px'
            }}>
              🚀
            </div>

            <h3 style={{
              color: '#1e293b',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '16px'
            }}>
              Need Help Scaling Your Protection?
            </h3>
            <p style={{
              color: '#64748b',
              fontSize: '18px',
              marginBottom: '32px',
              maxWidth: '700px',
              margin: '0 auto 32px',
              lineHeight: '1.6'
            }}>
              Our enterprise team is here to help you optimize your product authentication strategy, 
              integrate with your production systems, and scale across global markets.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <a
                href="mailto:karuoyamercy@gmail.com"
                className="hover-lift"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  color: 'white',
                  padding: '16px 32px',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Email Enterprise Support
              </a>
              
              <a
                href="tel:+254114445264"
                className="hover-lift"
                style={{
                  backgroundColor: 'white',
                  color: '#3b82f6',
                  padding: '16px 32px',
                  textDecoration: 'none',
                  border: '2px solid #3b82f6',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Call Direct Line
              </a>

              <a
                href="/manufacturer/help"
                className="hover-lift"
                style={{
                  backgroundColor: 'transparent',
                  color: '#64748b',
                  padding: '16px 32px',
                  textDecoration: 'none',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9,9h6v6H9z"/>
                </svg>
                Help Center
              </a>
            </div>

            {/* Additional Support Options */}
            <div style={{
              marginTop: '32px',
              paddingTop: '32px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'center',
              gap: '48px',
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#1e293b',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '4px'
                }}>
                  24/7
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Enterprise Support
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#1e293b',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '4px'
                }}>
                  &lt;2hrs
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Response Time
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#1e293b',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '4px'
                }}>
                  99.9%
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Platform Uptime
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManufacturerDashboard;



